import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Divider,
  FormControlLabel,
  Grid,
  Paper,
  Radio,
  RadioGroup,
  Snackbar,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import DashboardLayout from "../layouts/DashboardLayout";
import { AuthContext } from "../context/AuthContext";
import { useMobile } from "../context/MobileContext";
import { SOUND_PACK_DISCLAIMER, SOUND_PACK_OPTIONS, DASHBOARD_STYLE_OPTIONS } from "../constants/preferences";
import { SOUND_PACK_STORAGE_KEY, SOUND_PACK_SYNC_EVENT } from "../constants/audio";
import api from "../api/client";

export const DASHBOARD_STYLE_STORAGE_KEY = "ranger_dashboard_style";
export const DASHBOARD_STYLE_SYNC_EVENT = "ranger_dashboard_style_sync";
export const USE_TEN_CODES_STORAGE_KEY = "ranger_use_ten_codes";
export const USE_TEN_CODES_SYNC_EVENT = "ranger_use_ten_codes_sync";

function Settings() {
  const { user, refreshUser } = useContext(AuthContext);
  const { forceDesktopView, setForceDesktopView } = useMobile();
  const currentSoundPack = useMemo(() => (user?.soundPack || "male").toLowerCase(), [user?.soundPack]);
  const currentDashboardStyle = useMemo(() => (user?.dashboardStyle || "modern").toLowerCase(), [user?.dashboardStyle]);
  const currentUseTenCodes = useMemo(() => user?.useTenCodes || false, [user?.useTenCodes]);
  const [soundPack, setSoundPack] = useState(currentSoundPack);
  const [dashboardStyle, setDashboardStyle] = useState(currentDashboardStyle);
  const [useTenCodes, setUseTenCodes] = useState(currentUseTenCodes);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [snackbar, setSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");

  useEffect(() => {
    setSoundPack(currentSoundPack);
  }, [currentSoundPack]);

  useEffect(() => {
    setDashboardStyle(currentDashboardStyle);
  }, [currentDashboardStyle]);

  useEffect(() => {
    setUseTenCodes(currentUseTenCodes);
  }, [currentUseTenCodes]);

  const handleSoundPackChange = (event) => {
    setSoundPack(event.target.value);
    setError("");
  };

  const handleDashboardStyleChange = (event) => {
    setDashboardStyle(event.target.value);
    setError("");
  };

  const handleTenCodesToggle = async (event) => {
    const newValue = event.target.checked;
    setUseTenCodes(newValue);
    setSaving(true);
    setError("");
    try {
      await api.put("/users/preferences", { useTenCodes: newValue });
      if (typeof window !== "undefined") {
        window.localStorage.setItem(USE_TEN_CODES_STORAGE_KEY, newValue ? "true" : "false");
        window.dispatchEvent(new Event(USE_TEN_CODES_SYNC_EVENT));
      }
      await refreshUser?.();
      setSnackbarMessage(newValue ? "10-codes enabled." : "10-codes disabled.");
      setSnackbar(true);
    } catch (err) {
      setUseTenCodes(!newValue); // Revert on error
      const message = err?.response?.data?.message || err?.response?.data?.title || err?.response?.data || "Unable to update preference.";
      setError(typeof message === "string" ? message : "Unable to update preference.");
    } finally {
      setSaving(false);
    }
  };

  const handleSoundPackSubmit = async (event) => {
    event.preventDefault();
    if (saving || !soundPack) {
      return;
    }
    setSaving(true);
    setError("");
    try {
      await api.put("/users/preferences", { soundPack });
      if (typeof window !== "undefined") {
        window.localStorage.setItem(SOUND_PACK_STORAGE_KEY, soundPack);
        window.dispatchEvent(new Event(SOUND_PACK_SYNC_EVENT));
      }
      await refreshUser?.();
      setSnackbarMessage("Sound pack updated.");
      setSnackbar(true);
    } catch (err) {
      const message = err?.response?.data?.message || err?.response?.data?.title || err?.response?.data || "Unable to update sound pack.";
      setError(typeof message === "string" ? message : "Unable to update sound pack.");
    } finally {
      setSaving(false);
    }
  };

  const handleDashboardStyleSubmit = async (event) => {
    event.preventDefault();
    if (saving || !dashboardStyle) {
      return;
    }
    setSaving(true);
    setError("");
    try {
      await api.put("/users/preferences", { dashboardStyle });
      if (typeof window !== "undefined") {
        window.localStorage.setItem(DASHBOARD_STYLE_STORAGE_KEY, dashboardStyle);
        window.dispatchEvent(new Event(DASHBOARD_STYLE_SYNC_EVENT));
      }
      await refreshUser?.();
      setSnackbarMessage("Dashboard style updated.");
      setSnackbar(true);
    } catch (err) {
      const message = err?.response?.data?.message || err?.response?.data?.title || err?.response?.data || "Unable to update dashboard style.";
      setError(typeof message === "string" ? message : "Unable to update dashboard style.");
    } finally {
      setSaving(false);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar(false);
  };

  const hasSoundPackChanges = soundPack !== currentSoundPack;
  const hasDashboardStyleChanges = dashboardStyle !== currentDashboardStyle;

  return (
    <DashboardLayout title="Settings">
      <Grid container justifyContent="center">
        <Grid item xs={12} md={8}>
          <Stack spacing={3}>
            {/* Dashboard Style Section */}
            <Paper component="form" onSubmit={handleDashboardStyleSubmit} elevation={1} sx={{ p: 4 }}>
              <Stack spacing={3}>
                <Box>
                  <Typography variant="h5" gutterBottom>
                    Dashboard Style
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Choose between the modern interface or a retro-style dashboard reminiscent of late 90s/early 2000s police CAD/MDT systems.
                  </Typography>
                </Box>

                <Divider />

                <RadioGroup value={dashboardStyle} onChange={handleDashboardStyleChange}>
                  {DASHBOARD_STYLE_OPTIONS.map((option) => (
                    <FormControlLabel
                      key={option.value}
                      value={option.value}
                      control={<Radio />}
                      label={
                        <Box>
                          <Typography fontWeight={600}>{option.label}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {option.description}
                          </Typography>
                        </Box>
                      }
                    />
                  ))}
                </RadioGroup>

                {error && <Alert severity="error">{error}</Alert>}

                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                  <Button type="submit" variant="contained" disabled={!hasDashboardStyleChanges || saving}>
                    {saving ? "Saving..." : "Save Dashboard Style"}
                  </Button>
                  <Button
                    type="button"
                    variant="text"
                    disabled={!hasDashboardStyleChanges || saving}
                    onClick={() => {
                      setDashboardStyle(currentDashboardStyle);
                      setError("");
                    }}
                  >
                    Reset Selection
                  </Button>
                </Stack>
              </Stack>
            </Paper>

            {/* 10-Codes Section */}
            <Paper elevation={1} sx={{ p: 4 }}>
              <Stack spacing={3}>
                <Box>
                  <Typography variant="h5" gutterBottom>
                    10-Codes Display
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Replace button labels with police 10-codes for a more traditional dispatch experience.
                  </Typography>
                </Box>

                <Divider />

                <FormControlLabel
                  control={
                    <Switch
                      checked={useTenCodes}
                      onChange={handleTenCodesToggle}
                      disabled={saving}
                    />
                  }
                  label={
                    <Box>
                      <Typography fontWeight={600}>Use 10-Codes</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Shows 10-33 (Emergency), 10-7 (Out of Service), 10-8 (Available), 10-6 (Busy), 10-50 (Accident), etc.
                      </Typography>
                    </Box>
                  }
                />
              </Stack>
            </Paper>

            {/* View Preferences Section */}
            <Paper elevation={1} sx={{ p: 4 }}>
              <Stack spacing={3}>
                <Box>
                  <Typography variant="h5" gutterBottom>
                    View Preferences
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Control how RangerVault chooses the interface on touch devices and larger tablets.
                  </Typography>
                </Box>

                <Divider />

                <FormControlLabel
                  control={
                    <Switch
                      checked={forceDesktopView}
                      onChange={(event) => setForceDesktopView(event.target.checked)}
                    />
                  }
                  label={
                    <Box>
                      <Typography fontWeight={600}>Force Desktop View</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Keeps desktop layout enabled even on devices that would normally use the mobile dashboard.
                      </Typography>
                    </Box>
                  }
                />
              </Stack>
            </Paper>

            {/* Sound Pack Section */}
            <Paper component="form" onSubmit={handleSoundPackSubmit} elevation={1} sx={{ p: 4 }}>
              <Stack spacing={3}>
                <Box>
                  <Typography variant="h5" gutterBottom>
                    Sound Pack Preference
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Choose the alert voice or tone style you want to hear when RangerVault calls out incidents, assignments,
                    and safety prompts.
                  </Typography>
                </Box>

                <Divider />

                <RadioGroup value={soundPack} onChange={handleSoundPackChange}>
                  {SOUND_PACK_OPTIONS.map((option) => (
                    <FormControlLabel
                      key={option.value}
                      value={option.value}
                      control={<Radio />}
                      label={
                        <Box>
                          <Typography fontWeight={600}>{option.label}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {option.description}
                          </Typography>
                        </Box>
                      }
                    />
                  ))}
                </RadioGroup>

                <Alert severity="info">{SOUND_PACK_DISCLAIMER}</Alert>

                {error && <Alert severity="error">{error}</Alert>}

                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                  <Button type="submit" variant="contained" disabled={!hasSoundPackChanges || saving}>
                    {saving ? "Saving..." : "Save Sound Pack"}
                  </Button>
                  <Button
                    type="button"
                    variant="text"
                    disabled={!hasSoundPackChanges || saving}
                    onClick={() => {
                      setSoundPack(currentSoundPack);
                      setError("");
                    }}
                  >
                    Reset Selection
                  </Button>
                </Stack>
              </Stack>
            </Paper>
          </Stack>
        </Grid>
      </Grid>

      <Snackbar
        open={snackbar}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={handleSnackbarClose} severity="success" sx={{ width: "100%" }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
}

export default Settings;
